import * as z from 'zod/v4';
import { db } from '@/lib/firebase';
import {
  doc,
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  runTransaction,
  query,
  where,
} from 'firebase/firestore';
import type { CollectionReference, Query } from "firebase/firestore";

/**
 * Firestore document schema (no id)
 */
export const PrivacyRequestDocSchema = z.object({
  email: z.email({ error: 'Valid email is required' }),
  name: z.string().min(1, { error: 'Name is required' }),
  phone: z.string().min(5, { error: 'Phone number is required' }),
  message: z.string().min(1, { error: 'Message is required' }),
  friendly_id: z.number(),

  status: z.enum(['Open', 'Active', 'Closed', 'Declined', 'Completed']).default('Open'),

  metadata: z.object({
    created_at: z.date(),
    deleted_at: z.date().nullable(),
  }),
});

export const PrivacyRequestDocCreateSchema = PrivacyRequestDocSchema.omit({
  friendly_id: true,
  metadata: true,
})

export type PrivacyRequestDoc = z.infer<typeof PrivacyRequestDocSchema>;
export type PrivacyRequestWithId = PrivacyRequestDoc & { id: string };
export type PrivacyRequestCreateDoc = z.infer<typeof PrivacyRequestDocCreateSchema>;

/**
 * For updates we usually want "partial" fields + required id
 */
export const PrivacyRequestUpdateSchema = PrivacyRequestDocSchema.partial().extend({
  id: z.string(),
});

export type PrivacyRequestUpdate = z.infer<typeof PrivacyRequestUpdateSchema>;

function normalizeFromFirestore(data: any): PrivacyRequestDoc {
  const created_at = data.metadata?.created_at?.toDate?.() ?? data.metadata?.created_at;
  const deleted_at = data.metadata?.deleted_at?.toDate?.() ?? data.metadata?.deleted_at ?? null;

  return PrivacyRequestDocSchema.parse({
    ...data,
    metadata: {
      created_at,
      deleted_at,
    },
  });
}

/**
 * Helper: get the privacyRequests collection for a given customer
 * /customers/{customerId}/privacyRequests
 */
function privacyRequestsCollection(customerId: string) {
  const customerDocRef = doc(db, "customers", customerId);
  const colRef = collection(customerDocRef, "privacyRequests") as CollectionReference<PrivacyRequestWithId>;

  return colRef;
}

const idRefPath = (customerId: string) =>  doc(db, 'customers', customerId, 'id_refs', 'PRT');

//
// READ (all or one)
//
export async function read(
  customer: string,
  id: string | null = null,
  deleted: boolean = false,
): Promise<PrivacyRequestWithId | PrivacyRequestWithId[] | null> {
  const prCol = privacyRequestsCollection(customer);

  if (id) {
    const prDocRef = doc(prCol, id);
    const snap = await getDoc(prDocRef);
    if (!snap.exists()) return null;

    const parsed = normalizeFromFirestore(snap.data());
    return { id: snap.id, ...parsed };
  }

  const col = deleted
    ? prCol // no filter → return all docs
    : query(prCol, where("metadata.deleted_at", "==", null));

  const snap = await getDocs(col);

  return snap.docs
    .map((d) => {
      const parsed = normalizeFromFirestore(d.data());
      return { id: d.id, ...parsed };
    })
    .sort((a, b) => (b.friendly_id ?? 0) - (a.friendly_id ?? 0));
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

//
// CREATE (returns new ID)
//
export async function create(
  customer: string,
  item: PrivacyRequestCreateDoc
): Promise<PrivacyRequestWithId> {
  return runTransaction(db, async (tx) => {
    const counterRef = idRefPath(customer);           // DocumentReference
    const counterSnap = await tx.get(counterRef);     // DocumentSnapshot

    const currentID = counterSnap.exists()
      ? (counterSnap.data()?.['count'] ?? 0)
      : 0;

    const newID = currentID + 1;
    console.log('asd', 'currentid', currentID, newID);

    tx.set(counterRef, { count: newID }, { merge: true });

    const prCol = privacyRequestsCollection(customer);

    // Validate input with Zod (and apply defaults)
    const validated = PrivacyRequestDocCreateSchema.parse(item);

    const now = new Date();
    const toSave: PrivacyRequestDoc = {
      ...validated,
      friendly_id: newID,
      metadata: {
        created_at: validated.metadata?.created_at ?? now,
        deleted_at: validated.metadata?.deleted_at ?? null,
      },
    };

    const docRef = doc(prCol); // random ID
    tx.set(docRef, toSave);

    return { id: docRef.id, ...toSave };
  });
}

//
// UPDATE (partial update, requires id)
//
export async function update(
  customer: string,
  item: PrivacyRequestUpdate
): Promise<PrivacyRequestWithId> {
  const parsed = PrivacyRequestUpdateSchema.parse(item); // validates id + fields
  const prCol = privacyRequestsCollection(customer);

  const { id, ...updateData } = parsed;

  const prDocRef = doc(prCol, id);
  await updateDoc(prDocRef, updateData as any);

  // Read back the updated doc so we can normalize/validate
  const snap = await getDoc(prDocRef);
  if (!snap.exists()) {
    throw new Error(`Privacy request ${id} not found after update`);
  }

  const validated = normalizeFromFirestore(snap.data());
  return { id, ...validated };
}

//
// DELETE
//
export async function remove(
  customer: string,
  item: PrivacyRequestWithId | string,
  soft: boolean = true,
): Promise<{ id: string }> {
  const id = typeof item === 'string' ? item : item.id;
  if (!id) throw new Error('remove() requires an id');

  const prCol = privacyRequestsCollection(customer);
  const prDocRef = doc(prCol, id);

  if (soft) {
    // Soft delete → mark metadata.deleted_at
    await updateDoc(prDocRef, {
      "metadata.deleted_at": new Date(),
    });

    return { id };
  }

  // Example: these are the known subcollections for a privacyRequest
  const subcollectionNames = ['logs', 'events', 'attachments'];

  for (const name of subcollectionNames) {
    const subColRef = collection(prDocRef, name);
    const snap = await getDocs(subColRef);
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
  }

  await deleteDoc(prDocRef);

  return { id };
}
